import PhoneInput from 'react-phone-input-2';
import { KeyboardEvent, memo, ReactNode, RefObject, useContext } from 'react';
import styled from '@emotion/styled';
import { ThemeContext, css } from '@emotion/react';
import { spacing, fontSize } from 'presentations/views/styles';
import {
  Appearance,
  colorPalette,
  gray01,
  gray02,
} from 'presentations/views/styles/color';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import FormPaidLink from 'presentations/views/components/molecules/FormPaidLink';
import { PlanId } from 'domains/models/subscription/planId';

interface Props {
  country?: string;
  type?: string;
  label?: string;
  description?: string | ReactNode;
  prefix?: ReactNode | string;
  suffix?: ReactNode | string;
  className?: string;
  placeholder?: string;
  value?: string | number | null;
  defaultValue?: string | null;
  isFullWidth?: boolean;
  isContainerFullWidth?: boolean;
  width?: number;
  size?: 'medium' | 'small';
  variant?: 'outlined' | 'standard';
  isDisabled?: boolean;
  id?: string;
  name?: string;
  errors?: string[];
  isSelect?: boolean;
  isSelectNative?: boolean;
  helperText?: string;
  rows?: number;
  maxRows?: number;
  isMultiline?: boolean;
  children?: ReactNode;
  onChange?: any;
  onClick?: any;
  onBlur?: any;
  onKeyPress?: (e: KeyboardEvent) => void;
  onFocus?: any;
  isLoading?: boolean;
  isPaid?: boolean;
  isShowPaidLink?: boolean;
  plan?: PlanId;
  isRequired?: boolean;
  isNotRequired?: boolean;
  isAutoFocus?: boolean;
  innerRef?: RefObject<any> | null;
  inputRef?: RefObject<any> | null;
}

const Component = memo((props: Props) => {
  const errors = props.errors ? props.errors : [];
  const isError = errors.length > 0;
  const selectProps: any = {};
  if (props.isSelect === true) {
    if (props.isSelectNative === undefined) {
      selectProps['native'] = true;
    } else {
      selectProps['native'] = props.isSelectNative;
    }
  }

  let description = null;
  if (props.isPaid && props.isShowPaidLink) {
    description = (
      <PaidLink>
        <FormPaidLink description={props.description} plan={props.plan} />
      </PaidLink>
    );
  } else {
    if (props.description) {
      description = <Description>{props.description}</Description>;
    }
  }

  const context: any = useContext(ThemeContext);
  const colorMode = context.appearance === Appearance.Dark ? 'dark' : 'www';
  const backgroundColor = props.isDisabled
    ? colorPalette.gray05[colorMode]
    : colorPalette.white[colorMode];

  return (
    <Container
      fullWidth={props.isContainerFullWidth === true}
      onClick={props.onClick}
    >
      {props.label && (
        <FormLabel
          isRequired={props.isRequired}
          isPaid={props.isPaid}
          isShowPaidLink={props.isShowPaidLink}
          plan={props.plan}
          isNotRequired={props.isNotRequired}
        >
          {props.label}
        </FormLabel>
      )}
      {description}
      <PhoneInput
        masks={{
          jp: '...........',
        }}
        country={props.country || 'jp'}
        preferredCountries={['jp', 'us']}
        autoFormat={true}
        value={props.value === null ? undefined : String(props.value)}
        onChange={props.onChange}
        disabled={props.isDisabled}
        placeholder={props.placeholder}
        inputStyle={{ backgroundColor }}
      />
      {props.helperText && (
        <HelperText dangerouslySetInnerHTML={{ __html: props.helperText }} />
      )}
      {isError &&
        errors.map((errorText, i) => (
          <ErrorText key={i}>{errorText}</ErrorText>
        ))}
    </Container>
  );
});
export const Container = styled.span<{ fullWidth: boolean }>`
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

export const Description = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(16)};
`;
export const Prefix = styled.span`
  margin-right: ${spacing(8)};
  color: ${gray02};
`;
export const Suffix = styled.span`
  margin-left: ${spacing(8)};
  color: ${gray02};
`;
const HelperText = styled.div`
  font-size: ${fontSize(12)};
  margin-top: ${spacing(8)};
  color: ${gray01};
`;
const PaidLink = styled.div`
  margin-bottom: ${spacing(16)};
`;

export default Component;
